/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import AutocompleteInput from "../../../../Basis/Components/Autocomplete/AutocompleteInput";
import i18n from "../../../../Basis/Internationalisierung/i18n";
import { languages } from "../../../../Basis/Internationalisierung/languages";
import Secure from "../../../../Basis/Utils/Secure";
import StringUtil from "../../../../Basis/Utils/StringUtil";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nCountries";
import Region from "../../Region";
import { useRegionenContext } from "../RegionenContext";
import RegionInputProps from "./RegionInputProps";

function ermittleAnzeigeBezeichnung(istDeutsch: boolean, region?: Region): string {
    if (region) {
        const bezeichnung = istDeutsch
            ? region.bezeichnung ?? region.bezeichnungEN
            : region.bezeichnungEN ?? region.bezeichnung;
        if (StringUtil.equalsIgnoreCase(region.name, bezeichnung)) {
            return bezeichnung;
        }

        return `${region.name} (${bezeichnung})`
    }

    return "";
}

function istTreffer(suchbegriff: string, bezeichnung: string) {
    return bezeichnung.toLowerCase().includes(suchbegriff.toLowerCase());
}

export default function RegionInput(props: RegionInputProps) {
    const { landIso } = props;
    const { ermittleRegionen } = useRegionenContext();
    const [regionen, setRegionen] = React.useState<Region[]>([]);
    const { t } = useTranslation();
    const istDeutsch = props.isGermanTranslation || i18n.resolvedLanguage.startsWith(languages.de);

    Secure.useEffect((secure) => {
        (async () => {
            secure(setRegionen)(await ermittleRegionen(landIso));
        })();
    }, [landIso, ermittleRegionen])

    function executeQuery(suchbegriff: string) {
        return Promise.resolve(
            regionen
                .map(region => ermittleAnzeigeBezeichnung(istDeutsch, region))
                .filter(istTreffer.bind(null, suchbegriff))
                .sort()
        );
    }

    function handleChange(anzeigeBezeichnung: string) {
        const region = regionen.find(r => StringUtil.equalsIgnoreCase(
            ermittleAnzeigeBezeichnung(istDeutsch, r), anzeigeBezeichnung));
        props.onChange(region?.name ?? anzeigeBezeichnung);
    }

    return (
        <AutocompleteInput
            {...props}
            caption={props.caption}
            value={props.value ?? ""}
            onChange={handleChange}
            executeQuery={executeQuery}
            typBezeichnung={props.typBezeichnung}
            feldBezeichnung={props.feldBezeichnung}
            readonly={props.readonly}
            placeholder={t(translationKeys.regionInput.placeholder)}
            tooltip={props.tooltip}
            ignoreTooltip={props.ignoreTooltip}
            required={props.required}
            alwaysExecuteQueryOnOpen
        />
    );
}
